// strategy/icm.ts - the ICM layer (ENGINE_DESIGN.md §6.5). Wraps @poker-apprentice/icm-calculator
// (MIT) for tournament $EV, and adds the risk-premium → icmPressure bucketing the exploit rules
// (R5, ICM_*) and grading consume.
//
// The library returns $EV per stack, index-aligned. Its `calculate()` drops to Monte-Carlo above ~30
// players - which is NON-deterministic and would break the engine's determinism invariant - so for
// the ≤30-player fields v1 ever sees (final tables are ≤9) we pin to `calculateExact`, the exact
// Malmuth-Harville dynamic program, and only fall back to MC beyond the exact cap. We trust the
// library for the equity itself (it is tested upstream; note it rounds equities to 2 decimals, so
// the prize pool conserves only to ~±0.01) and prove OUR layer - the risk premium and its bucketing
// - in icm.test.ts.
import { calculate, calculateExact } from '@poker-apprentice/icm-calculator';
/** Above this player count exact ICM is infeasible and the library uses (non-deterministic) MC. */
const MAX_EXACT_PLAYERS = 30;
/** The raw library call - valid only when every stack is live (> 0). See `icmEquity`. */
const icmEquityAllLive = (chipStacks, payouts) => chipStacks.length <= MAX_EXACT_PLAYERS
    ? calculateExact({ chipStacks, payouts })
    : calculate({ chipStacks, payouts });
/**
 * Tournament $EV per stack (index-aligned to `chipStacks`). Exact + deterministic ≤30 players; MC
 * above (out of v1 scope - no final table is that large).
 *
 * BUSTED STACKS (ST2). A stack of 0 is a player who has finished, and the Malmuth-Harville dynamic
 * program cannot place them: with a FULLY-PAID field (payouts ≥ players - which is what a final table
 * inherently is: 3 left of 3 paid) `calculateExact` returns NaN for the busted index. That NaN is not
 * a modelling limit, it is a library artifact - the real answer is elementary. So we resolve busts
 * ourselves and only hand the library a field where everyone is live:
 *
 *   • `k` busted stacks finish in the BOTTOM k places (L+1..n, where L = live count), so they collect
 *     `payouts[L .. n-1]`. Simultaneous busts tie, and split those places' prizes evenly (the fair ICM
 *     expectation of a same-hand knockout; a live-poker tiebreak by starting stack is not modelled).
 *   • the `L` live stacks contest the TOP L places only - `payouts[0 .. L-1]`.
 *
 * This is a strict GENERALIZATION, not a behavior change: when the field has bust room
 * (payouts < players) the bottom slice is empty, a busted stack collects 0, and the live players'
 * equities are what the library already returned. It only adds an answer where there was a NaN.
 * (Supersedes the DT SOURCES.md §1c "needs prizes < players" restriction - final-table ICM, ST2's
 * whole point, is a fully-paid field by definition.)
 */
export const icmEquity = (chipStacks, payouts) => {
    const live = [];
    const busted = [];
    chipStacks.forEach((chips, i) => (chips > 0 ? live : busted).push(i));
    if (busted.length === 0)
        return icmEquityAllLive(chipStacks, payouts);
    if (live.length === 0)
        throw new RangeError('icmEquity: every stack is 0 - no live field to place');
    // The busted tail: places L+1..n. Empty when the field has bust room, giving the classic 0.
    const tail = payouts.slice(live.length, live.length + busted.length);
    const bustedShare = tail.length ? tail.reduce((a, b) => a + b, 0) / busted.length : 0;
    // The live field, contesting the top L prizes. One survivor takes 1st outright.
    const liveEquities = live.length === 1
        ? [payouts[0] ?? 0]
        : icmEquityAllLive(live.map((i) => chipStacks[i]), payouts.slice(0, live.length));
    const out = new Array(chipStacks.length).fill(0);
    busted.forEach((i) => (out[i] = bustedShare));
    live.forEach((i, k) => (out[i] = liveEquities[k]));
    return out;
};
/**
 * Risk premium = the EXTRA equity (beyond the chip-EV breakeven) hero needs to profitably call the
 * all-in, once ICM is priced in. Positive ⇒ ICM makes the call worse than the chips say (the normal
 * bubble case: pay jumps punish busting). Near zero ⇒ chip-EV and $EV agree.
 *
 *   p_chip = atRisk / (2·atRisk + dead)                     chip-EV breakeven equity to call
 *   p_icm  = ($EV_fold − $EV_lose) / ($EV_win − $EV_lose)   $EV breakeven equity to call
 *   riskPremium = p_icm − p_chip
 *
 * where the win/lose/fold stacks are chip-conserving transfers of `atRisk` (+ dead money to the
 * winner) between hero and villain. Heads-up all-in scope; multiway is deferred (§6.5).
 */
export const riskPremium = (spot) => {
    const { chipStacks, payouts, heroIndex, villainIndex, atRisk } = spot;
    const dead = spot.deadMoney ?? 0;
    const pChip = atRisk / (2 * atRisk + dead);
    // Fold: hero keeps his stack; the dead money is conceded to the villain.
    const foldStacks = [...chipStacks];
    foldStacks[villainIndex] += dead;
    // Call & win: hero collects villain's at-risk chips + the dead money; villain loses his at-risk.
    const winStacks = [...chipStacks];
    winStacks[heroIndex] += atRisk + dead;
    winStacks[villainIndex] -= atRisk;
    // Call & lose: hero loses his at-risk chips; villain collects them + the dead money.
    const loseStacks = [...chipStacks];
    loseStacks[heroIndex] -= atRisk;
    loseStacks[villainIndex] += atRisk + dead;
    const evFold = icmEquity(foldStacks, payouts)[heroIndex];
    const evWin = icmEquity(winStacks, payouts)[heroIndex];
    const evLose = icmEquity(loseStacks, payouts)[heroIndex];
    const denom = evWin - evLose;
    if (denom <= 0)
        return 0; // degenerate (no ICM difference between win and lose); no premium.
    const pIcm = (evFold - evLose) / denom;
    return pIcm - pChip;
};
/** Risk-premium magnitude → pressure bucket the rules key on. Thresholds are ours (test-pinned): a
 *  premium under 5 equity points is noise ('none'); 5–12 points is real ('moderate'); above 12
 *  points is a bubble/pay-jump squeeze ('severe'). We bucket on the SIGNED premium - only a positive
 *  premium (ICM makes the call worse) is pressure; a negative one (chips understate the call) is not.
 *
 *  A non-finite premium THROWS (ST2). Both comparisons below are false for NaN, so an un-guarded NaN
 *  would fall through to 'severe' - a wrong answer wearing a plausible label, since the spots that
 *  used to NaN (final tables) really are high-pressure, and a flat/zero-pressure structure would have
 *  been labelled 'severe' just the same. Fail loudly instead of grading off a NaN. */
export const icmPressureBucket = (premium) => {
    if (!Number.isFinite(premium))
        throw new RangeError(`icmPressureBucket: risk premium must be finite, got ${premium}. A non-finite premium means ` +
            `the ICM inputs were degenerate - bucketing it would silently report 'severe'.`);
    if (premium < 0.05)
        return 'none';
    if (premium < 0.12)
        return 'moderate';
    return 'severe';
};
/** Convenience: the pressure bucket for an all-in spot directly. */
export const icmPressureFor = (spot) => icmPressureBucket(riskPremium(spot));
//# sourceMappingURL=icm.js.map